import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Trash2, AlertTriangle, Crown, Calendar, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountSettings() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [working, setWorking] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/subscription`, { withCredentials: true })
      .then(res => setSubscription(res.data.has_subscription ? res.data.subscription : null))
      .catch(() => {});
  }, []);

  const cancelSubscription = async () => {
    setWorking(true);
    try {
      const res = await axios.post(`${API}/payments/cancel-subscription`, {}, { withCredentials: true });
      toast.success('Pro will end at the end of your current billing cycle');
      setConfirmCancel(false);
      if (refreshUser) await refreshUser();
      // re-fetch
      const subRes = await axios.get(`${API}/payments/subscription`, { withCredentials: true });
      setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancellation failed');
    } finally { setWorking(false); }
  };

  const exportData = async () => {
    setWorking(true);
    try {
      const res = await axios.get(`${API}/user/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fokyes-data-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch { toast.error('Export failed'); }
    finally { setWorking(false); }
  };

  const deleteAccount = async () => {
    if (confirmText !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    setWorking(true);
    try {
      await axios.delete(`${API}/user/account`, { withCredentials: true });
      toast.success('Account deleted');
      await logout();
      navigate('/', { replace: true });
    } catch { toast.error('Failed'); setWorking(false); }
  };

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-1">Account</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>
        </div>
      </div>

      <div className="container-swiss py-8 max-w-2xl space-y-8">
        <section className="border border-[#09090B] p-6">
          <h2 className="mono-data text-sm text-[#09090B] mb-4">Profile</h2>
          <div className="space-y-2 text-sm">
            <p><span className="mono-data text-xs text-zinc-500">Email:</span> {user?.email}</p>
            <p><span className="mono-data text-xs text-zinc-500">Name:</span> {user?.name}</p>
            <p><span className="mono-data text-xs text-zinc-500">Plan:</span> {user?.subscription?.plan || 'free'}</p>
            <p><span className="mono-data text-xs text-zinc-500">Account:</span> {user?.account_status || 'active'}</p>
          </div>
        </section>

        {subscription && (
          <section className="border border-[#09090B] p-6" data-testid="subscription-section">
            <h2 className="mono-data text-sm text-[#09090B] mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4" /> Pro Subscription
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-3">
                <span className="mono-data text-xs text-zinc-500">STATUS</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                  subscription.status === 'active' ? 'bg-green-100 text-[#09090B]' :
                  subscription.status === 'cancelled' ? 'bg-amber-100 text-zinc-700' :
                  subscription.status === 'halted' ? 'bg-zinc-200 text-[#09090B]' :
                  'bg-zinc-100 text-zinc-700'
                }`} data-testid="sub-status">{subscription.status}</span>
              </div>
              {subscription.current_period_end && (
                <div className="flex items-center justify-between border-b border-zinc-200 pb-3">
                  <span className="mono-data text-xs text-zinc-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {subscription.cancel_at_period_end ? 'ENDS ON' : 'NEXT BILLING'}
                  </span>
                  <span className="font-mono text-sm" data-testid="sub-period-end">
                    {new Date(subscription.current_period_end).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric'})}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="mono-data text-xs text-zinc-500">CYCLES PAID</span>
                <span className="font-mono text-sm">{subscription.paid_count || 0} / {subscription.total_count || 12}</span>
              </div>
            </div>

            {subscription.status === 'active' && !subscription.cancel_at_period_end && (
              <div className="mt-6 pt-4 border-t border-zinc-200">
                {!confirmCancel ? (
                  <button onClick={() => setConfirmCancel(true)}
                    className="text-xs text-zinc-500 hover:text-[#09090B] flex items-center gap-2"
                    data-testid="cancel-sub-btn">
                    <X className="w-3 h-3" /> Cancel subscription
                  </button>
                ) : (
                  <div className="bg-zinc-50 p-4 border border-zinc-200">
                    <p className="text-xs text-zinc-700 mb-3">
                      You'll keep Pro access until <strong>{new Date(subscription.current_period_end).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric'})}</strong>, then auto-downgrade to Free. No further charges.
                    </p>
                    <div className="flex gap-2">
                      <button onClick={cancelSubscription} disabled={working}
                        className="bg-[#09090B] text-white text-xs px-4 py-2 disabled:opacity-40"
                        data-testid="confirm-cancel-sub-btn">
                        {working ? 'Cancelling…' : 'Confirm cancellation'}
                      </button>
                      <button onClick={() => setConfirmCancel(false)}
                        className="text-xs px-4 py-2 border border-zinc-300">
                        Keep Pro
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {subscription.cancel_at_period_end && (
              <div className="mt-4 p-3 bg-zinc-100 border-l-2 border-zinc-400 text-xs">
                Subscription will end on {new Date(subscription.current_period_end).toLocaleDateString('en-IN')}. You can re-subscribe anytime from <a href="/pricing" className="text-[#09090B] underline">Pricing</a>.
              </div>
            )}
          </section>
        )}

        <section className="border border-[#09090B] p-6" data-testid="export-section">
          <h2 className="mono-data text-sm text-[#09090B] mb-2">Your Data</h2>
          <p className="text-sm text-zinc-600 mb-4">
            Download a complete JSON export of your account data. (DPDP § 11, GDPR Art. 20)
          </p>
          <button onClick={exportData} disabled={working}
            className="btn-primary text-xs px-6 py-2 flex items-center gap-2" data-testid="export-data-btn">
            <Download className="w-3 h-3" /> Export my data
          </button>
        </section>

        <section className="border border-[#09090B] p-6" data-testid="danger-section">
          <h2 className="mono-data text-sm text-[#09090B] mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Danger Zone
          </h2>
          <p className="text-sm text-zinc-600 mb-4">
            Permanently delete your account, all session attempts, BrainMap data, parent-links, and screen-time logs.
            Consent audit records are retained for 7 years per DPDP Rule 5. This cannot be undone.
          </p>

          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              className="btn-secondary text-xs px-6 py-2 flex items-center gap-2 border-[#09090B] text-[#09090B] hover:bg-zinc-100"
              data-testid="delete-account-btn">
              <Trash2 className="w-3 h-3" /> Delete my account
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[#09090B] font-bold">Type DELETE to confirm</p>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
                className="input-swiss text-sm max-w-xs" placeholder="DELETE" data-testid="delete-confirm-input" />
              <div className="flex gap-3">
                <button onClick={deleteAccount} disabled={confirmText !== 'DELETE' || working}
                  className="btn-primary bg-[#09090B] text-xs px-6 py-2 disabled:opacity-40" data-testid="confirm-delete-btn">
                  Permanently Delete
                </button>
                <button onClick={() => { setConfirmDelete(false); setConfirmText(''); }}
                  className="btn-secondary text-xs px-6 py-2">Cancel</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
